import {
  IonButton,
  IonContent,
  IonIcon,
  IonPage,
  IonText,
  IonTitle,
} from '@ionic/react';
import { alertCircleOutline, refreshOutline } from 'ionicons/icons';
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <IonPage>
          <IonContent className="ion-padding">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                padding: '20px',
              }}
            >
              <IonIcon
                icon={alertCircleOutline}
                color="danger"
                style={{ fontSize: '64px', marginBottom: '20px' }}
              />
              <IonTitle color="danger">
                <h2>Something went wrong</h2>
              </IonTitle>
              <IonText
                color="medium"
                style={{ marginBottom: '30px', maxWidth: '500px' }}
              >
                <p>
                  We encountered an unexpected error. This might be a temporary
                  issue. You can try refreshing the page or going back to the
                  previous screen.
                </p>
              </IonText>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <IonButton onClick={this.handleRetry} color="primary">
                  <IonIcon icon={refreshOutline} slot="start" />
                  Try Again
                </IonButton>
                <IonButton
                  onClick={() => window.history.back()}
                  fill="outline"
                  color="medium"
                >
                  Go Back
                </IonButton>
              </div>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details
                  style={{
                    marginTop: '20px',
                    textAlign: 'left',
                    maxWidth: '500px',
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      color: 'var(--ion-color-medium)',
                    }}
                  >
                    Error Details (Development)
                  </summary>
                  <pre
                    style={{
                      background: 'var(--ion-color-light)',
                      padding: '10px',
                      borderRadius: '5px',
                      fontSize: '12px',
                      overflow: 'auto',
                    }}
                  >
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}
            </div>
          </IonContent>
        </IonPage>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
